import type { VisitView } from "../../../server/modules/homecare/visits";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { card, chip, ib, table } from "../../ui/record";
import { area, field, pills, refuse } from "../clients/forms";
import { flagChips } from "../patients/visits";

// The caregiver's Availability card and Add availability sheet (brief 1D-1, mockup availCard and
// M.type "avail"): set once, pick the shift, then the days it repeats (On these weekdays with
// Mon–Fri and Weekends, Every day of a month, Every day between dates; From, Until). Each one is a
// row with Remove (it's kept, with why); the month's calendar marks the days available and the
// days scheduled. Under it, the caregiver's upcoming visits. Set by the office here; the caregiver
// sets their own in the field app (1D-2).

interface Answer {
  person: { id: string; name: string };
  canEdit: boolean;
  availability: { id: string; words: string; shift: string }[];
  month: {
    label: string;
    /** Blank cells before the 1st, and the weekday names, from the company's week start. */
    firstDow: number;
    weekdays?: string[];
    days: number;
    marks: Record<string, string[]>;
    scheduled: number[];
  };
  upcoming: VisitView[];
  shifts: { value: string; label: string; name: string }[];
  repeats: { value: string; label: string }[];
  error?: string;
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const today = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** The month (mockup calMonth): each day available is tinted with its shifts' dots; scheduled outlined. */
function calendar(a: Answer): HTMLElement {
  const names = a.shifts.map((s) => s.name);
  const dot = (name: string) => {
    const i = names.indexOf(name);
    return el("i", { class: `cal-dot s${i < 0 ? 1 : i % 3}`, "aria-hidden": "true" });
  };
  const cells: HTMLElement[] = [];
  for (let i = 0; i < a.month.firstDow; i++) cells.push(el("div"));
  for (let d = 1; d <= a.month.days; d++) {
    const marks = a.month.marks[String(d)] ?? [];
    const sched = a.month.scheduled.includes(d);
    cells.push(
      el(
        "div",
        {
          class: `cal-d${marks.length ? " on" : ""}${sched ? " sched" : ""}`,
          title: `${marks.join(", ") || "Not available"}${sched ? " · scheduled" : ""}`,
        },
        el("b", {}, String(d)),
        ...[...new Set(marks)].map(dot),
      ),
    );
  }
  return el(
    "div",
    {},
    el("div", { class: "eyebrow", style: "margin-top:10px" }, a.month.label),
    el(
      "div",
      { class: "cal", "aria-label": `Availability in ${a.month.label}` },
      el("div", { class: "cal-h" }, ...(a.month.weekdays ?? DOW).map((x) => el("span", {}, x))),
      el("div", { class: "cal-g" }, ...cells),
    ),
    el(
      "div",
      { class: "cal-legend" },
      ...a.shifts
        .filter((s) => s.value !== "custom")
        .slice(0, 3)
        .map((s) => el("span", {}, dot(s.name), s.name)),
      el(
        "span",
        {},
        el("i", { class: "dotk", style: "outline:2px solid var(--primary);background:none" }),
        "Scheduled",
      ),
    ),
  );
}

/** The Availability card and the upcoming visits, drawn into `host`; reloads after each change. */
export async function availabilityCard(host: HTMLElement, personId: string) {
  async function reload() {
    const res = await api<Answer>(
      "GET",
      `/api/people/${encodeURIComponent(personId)}/availability`,
    );
    if (!res.ok) {
      host.replaceChildren();
      return;
    }
    draw(res.body);
  }

  function draw(a: Answer) {
    const add = el(
      "button",
      { type: "button", class: "btn sm pri" },
      icon("plus"),
      " Add availability",
    );
    add.addEventListener("click", () => addAvailability(a, reload));
    const rows = a.availability.length
      ? a.availability.map((r) =>
          el(
            "div",
            { class: "prow" },
            el("span", {}, r.words),
            a.canEdit
              ? ib("trash", "Remove this availability", () => removeOne(a, r, reload))
              : null,
          ),
        )
      : [el("p", { class: "muted" }, "No availability yet.")];
    const avail = card(
      "Availability",
      "Set once: pick the shift, then the days it repeats.",
      a.canEdit ? [add] : [],
      el("div", {}, ...rows),
      calendar(a),
    );
    const upcoming = card(
      "Upcoming visits",
      null,
      [],
      a.upcoming.length
        ? table(
            [
              {
                h: "When",
                f: (v: VisitView) => el("span", {}, el("b", {}, v.when), flagChips(v.flags)),
              },
              { h: "Patient", f: (v) => `${v.patient.name} · ${v.service}` },
              { h: "Stage", f: (v) => chip(v.stage, "info") },
            ],
            a.upcoming,
            undefined,
            "Upcoming visits",
          )
        : el("p", { class: "muted" }, "No visits scheduled."),
    );
    avail.setAttribute("aria-label", "Availability");
    upcoming.setAttribute("aria-label", "Upcoming visits");
    host.replaceChildren(avail, upcoming);
  }
  await reload();
}

function addAvailability(a: Answer, done: () => Promise<void>) {
  const shift = pills(
    "Shift",
    "radio",
    a.shifts.map((s) => [s.value, s.label] as const),
    [a.shifts.find((s) => s.name === "Nights")?.value ?? a.shifts[0]?.value ?? "custom"],
  );
  const start = field("Start", "08:00", { type: "time" });
  const end = field("End", "14:00", { type: "time" });
  const custom = el("div", { class: "formgrid wide" }, start.el, end.el);
  const repeat = pills(
    "Repeat",
    "radio",
    a.repeats.map((r) => [r.value, r.label] as const),
    ["weekdays"],
  );
  const days = pills(
    "On these weekdays",
    "checkbox",
    DOW.map((d, i) => [String(i), d] as const),
    ["0", "2", "4"],
  );
  const quick = (label: string, set: string[]) => {
    const b = el("button", { type: "button", class: "btn sm ghost" }, label);
    b.addEventListener("click", () => {
      for (const i of days.inputs) i.checked = set.includes(i.value);
      void preview();
    });
    return b;
  };
  const quicks = el(
    "div",
    { class: "wide", style: "display:flex;gap:6px" },
    quick("Mon–Fri", ["1", "2", "3", "4", "5"]),
    quick("Weekends", ["0", "6"]),
  );
  const from = field("From", today(), { type: "date" });
  const until = field("Until", "", { type: "date", placeholder: "No end" });
  const prev = el("div", { "aria-live": "polite" });
  const draw = () => {
    // Not `hidden`: a field's own display would win over it.
    custom.style.display = shift.values()[0] === "custom" ? "" : "none";
    const weekly = repeat.values()[0] === "weekdays";
    days.el.style.display = weekly ? "" : "none";
    quicks.style.display = weekly ? "flex" : "none";
    until.el.style.display = repeat.values()[0] === "month" ? "none" : "";
    void preview();
  };
  // The preview (mockup availPrev): how many days in the From day's month, as the card will say it.
  async function preview() {
    const r = repeat.values()[0];
    const n = (() => {
      const f = from.input.value;
      if (!f) return 0;
      const y = +f.slice(0, 4);
      const m = +f.slice(5, 7);
      const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
      const picked = days.values().map(Number);
      let count = 0;
      for (let d = 1; d <= last; d++) {
        const day = `${f.slice(0, 8)}${String(d).padStart(2, "0")}`;
        if (r !== "month" && day < f) continue;
        if (r !== "month" && until.input.value && day > until.input.value) continue;
        if (r === "weekdays" && !picked.includes(new Date(`${day}T12:00:00Z`).getUTCDay()))
          continue;
        count++;
      }
      return count;
    })();
    const month = from.input.value
      ? new Intl.DateTimeFormat("en-US", { month: "long", timeZone: "UTC" }).format(
          new Date(`${from.input.value}T12:00:00Z`),
        )
      : "";
    const label = a.shifts.find((s) => s.value === shift.values()[0])?.name ?? "";
    prev.replaceChildren(
      el("div", { class: "note" }, el("p", {}, `${n} days in ${month}: ${label}`)),
    );
  }
  for (const i of [...shift.inputs, ...repeat.inputs]) i.addEventListener("change", draw);
  for (const i of [...days.inputs, from.input, until.input])
    i.addEventListener("change", () => void preview());
  const save = el(
    "button",
    { type: "submit", class: "btn pri" },
    icon("check"),
    " Save availability",
  );
  const cancel = el("button", { type: "button", class: "btn" }, "Cancel");
  const m = openModal({
    title: "Add availability",
    sub: "Pick the shift, then where it repeats.",
    body: [
      el(
        "form",
        { class: "form", style: "max-width:none" },
        shift.el,
        custom,
        repeat.el,
        days.el,
        quicks,
        el("div", { class: "formgrid" }, from.el, until.el),
        prev,
      ),
    ],
    actions: [save, cancel],
  });
  cancel.addEventListener("click", () => m.close());
  draw();
  save.addEventListener("click", async (e) => {
    e.preventDefault();
    const res = await api<Answer>("POST", `/api/people/${a.person.id}/availability`, {
      shift: shift.values()[0],
      start: start.input.value,
      end: end.input.value,
      repeat: repeat.values()[0],
      days: days.values().map(Number),
      startsOn: from.input.value,
      endsOn: until.input.value,
    });
    if (!res.ok) return refuse(m.note, res.body.error);
    m.close();
    toast("Availability saved", "Used for scheduling");
    await done();
  });
}

function removeOne(a: Answer, r: Answer["availability"][number], done: () => Promise<void>) {
  const why = area("Why", "");
  const go = el("button", { type: "submit", class: "btn pri" }, icon("trash"), " Remove");
  const m = openModal({
    title: "Remove this availability",
    sub: `${r.words}.`,
    body: [el("form", { class: "form", style: "max-width:none" }, why.el)],
    actions: [go],
  });
  go.addEventListener("click", async (e) => {
    e.preventDefault();
    const res = await api<Answer>(
      "POST",
      `/api/people/${a.person.id}/availability/${r.id}/remove`,
      { why: why.input.value },
    );
    if (!res.ok) return refuse(m.note, res.body.error);
    m.close();
    toast("Availability removed");
    await done();
  });
}

// ---- The Matching card (brief 1D-2) ---------------------------------------------------------------

interface Traits {
  canEdit: boolean;
  fields: {
    key: string;
    label: string;
    answerType: "yes_no" | "text" | "choice" | "number" | "list";
    options: string[];
    value: unknown;
    words: string;
  }[];
  error?: string;
}

/**
 * The caregiver's side of matching: one answer per trait field (languages, gender, OK with pets,
 * OK with smoking, Hoyer lift, how far they'll travel, and any field the company adds in Settings ›
 * Home Care › Scheduling). Each answer saves as it changes, in the history.
 */
export async function matchingCard(host: HTMLElement, personId: string) {
  const res = await api<Traits>("GET", `/api/people/${personId}/traits`);
  if (!res.ok) return host.replaceChildren();
  const t = res.body;
  const save = async (key: string, value: unknown) => {
    const r = await api<Traits>("PUT", `/api/people/${personId}/traits`, {
      values: { [key]: value },
    });
    if (!r.ok) return toast("That wasn't saved", r.body.error);
    toast("Saved", "Suggested caregivers use it at once");
  };
  const input = (f: Traits["fields"][number]) => {
    const id = `tr-${f.key.replace(/\W/g, "")}`;
    let i: HTMLInputElement | HTMLSelectElement;
    if (f.answerType === "yes_no" || f.answerType === "choice") {
      const opts: [string, string][] =
        f.answerType === "yes_no"
          ? [
              ["", "Not on file"],
              ["yes", "Yes"],
              ["no", "No"],
            ]
          : [["", "Not on file"], ...f.options.map((o): [string, string] => [o, o])];
      const now =
        f.value === true
          ? "yes"
          : f.value === false
            ? "no"
            : f.value == null
              ? ""
              : String(f.value);
      i = el(
        "select",
        { id },
        ...opts.map(([v, l]) => el("option", { value: v, selected: v === now }, l)),
      );
    } else
      i = el("input", {
        id,
        type: f.answerType === "number" ? "number" : "text",
        value: f.words,
        placeholder: f.answerType === "list" ? "Separate with commas" : "",
      });
    if (!t.canEdit) i.setAttribute("disabled", "");
    i.addEventListener("change", () => void save(f.key, i.value === "" ? null : i.value));
    return el("div", { class: "field" }, el("label", { for: id }, f.label), i);
  };
  host.replaceChildren(
    card(
      el("span", {}, icon("zap"), " Matching"),
      "What the caregiver's profile says, for Suggested caregivers",
      [],
      el("div", { class: "form formgrid", style: "max-width:none" }, ...t.fields.map(input)),
      el("p", { class: "small muted" }, chip("Set in Settings › Home Care › Scheduling", "plain")),
    ),
  );
}
